import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { axios, errorResponse } from "../../../helpers";
import { loginData } from "../../../stores/auth";
import "../style/transactionDetail.scss";
import { FiArrowLeft, FiPackage, FiTruck, FiCheckCircle, FiXCircle, FiCheck } from "react-icons/fi";

const TransactionDetail = () => {
    const { transactionId } = useParams();
    const navigate = useNavigate();
    const dataLogin = useSelector(loginData);
    const [transaction, setTransaction] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    console.log("Transaction ID from params:", transaction);
    const fetchDetail = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/v1/transactions/${transactionId}`, {
                headers: { Authorization: `Bearer ${dataLogin.data.token}` },
            });

            if (response.data.success) {
                setTransaction(response.data.data);
                setError("");
            } else {
                setError(response.data.message || "Failed to load transaction detail");
            }
        } catch (err) {
            setError(errorResponse(err) || "Error loading detail");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDetail();
    }, [transactionId]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat("id-ID", {
            style: "currency",
            currency: "IDR",
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const getPaymentStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case "paid":
            case "completed":
                return <FiCheckCircle className="icon-success" />;
            case "pending":
                return <FiPackage className="icon-warning" />;
            case "settlement":
                return <FiCheck className="icon-success" />;
            default:
                return <FiXCircle className="icon-danger" />;
        }
    };

    const getPaymentStatusClass = (status) => {
        switch (status?.toLowerCase()) {
            case "paid":
            case "completed":
                return "status-badge success";
            case "pending":
                return "status-badge warning";
            case "failed":
                return "status-badge danger";
            default:
                return "status-badge default";
        }
    };

    if (loading) return <div className="loading">Loading detail…</div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!transaction) return <div>No transaction found</div>;

    return (
        <div className="transaction-detail">
            {/* Header with Back Button */}
            <div className="detail-header">
                <button className="back-button" onClick={() => navigate(-1)}>
                    <FiArrowLeft /> Back
                </button>
                <h2>Transaction Detail</h2>
            </div>

            <div className="detail-content">
                {/* Left Column */}
                <div className="left-column">
                    {/* Transaction Info Card */}
                    <div className="info-card">
                        <h3 className="card-title">
                            <FiPackage /> Transaction Info
                        </h3>
                        <div className="info-grid">
                            <div className="info-item">
                                <span className="info-label">Transaction ID</span>
                                <span className="info-value">{transaction.transaction_id}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Transaction Date</span>
                                <span className="info-value">
                                    {new Date(transaction.transaction_date).toLocaleDateString("id-ID", {
                                        weekday: 'long',
                                        year: 'numeric',
                                        month: 'long',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    })}
                                </span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Order Type</span>
                                <span className="info-value">{transaction.order_type}</span>
                            </div>
                            <div className="info-item">
                                <span className="info-label">Payment Status</span>
                                <span className={getPaymentStatusClass(transaction.payment_status)}>
                                    {getPaymentStatusIcon(transaction.payment_status)}
                                    {transaction.payment_status}
                                </span>
                            </div>
                            {transaction.order_status && (
                                <div className="info-item">
                                    <span className="info-label">Order Status</span>
                                    <span className="status-badge info">{transaction.order_status}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Shipping Address Card */}
                    <div className="info-card">
                        <h3 className="card-title">
                            <FiTruck /> Shipping Address (Buyer)
                        </h3>
                        <div className="address-content">
                            {transaction.address ? (
                                <>
                                    <p className="address-name">{transaction.address.recipient_name || "N/A"}</p>
                                    <p className="address-phone">{transaction.address.phone || "N/A"}</p>
                                    <p className="address-detail">{transaction.address.full_address || "N/A"}</p>
                                    <p className="address-detail">{transaction.address.state?.name || "N/A"}</p>
                                    <p className="address-region">
                                        {transaction.address.city?.name}, {transaction.address.province?.name}
                                        {transaction.address.postal_code && ` - ${transaction.address.postal_code}`}
                                    </p>
                                </>
                            ) : (
                                <p className="no-address">No shipping address available</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div className="right-column">
                    {/* Order Items Card */}
                    <div className="info-card">
                        <h3 className="card-title">Order Item</h3>
                        <div className="order-items">
                            {transaction.order_details && transaction.order_details.length > 0 ? (
                                <div className="items-table">
                                    <div className="table-header">
                                        <div className="col-product">Product</div>
                                        <div className="col-qty">Qty</div>
                                        <div className="col-price">Price</div>
                                        <div className="col-subtotal">Subtotal</div>
                                    </div>
                                    <div className="table-body">
                                        {transaction.order_details.map((item, index) => (
                                            <div className="table-row" key={index}>
                                                <div className="col-product">
                                                    <div className="product-name">{item.fabric.fabric_name || "Product"}</div>
                                                    {/* {item.variant && (
                                                        <div className="product-variant">Variant: {item.variant}</div>
                                                    )} */}
                                                </div>
                                                <div className="col-qty">{item.qty || 1}</div>
                                                <div className="col-price">{formatCurrency(item.price || 0)}</div>
                                                <div className="col-subtotal">
                                                    {formatCurrency((item.price || 0) * (item.quantity || 1))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <p className="no-items">No items found</p>
                            )}
                        </div>
                    </div>

                    {/* Order Summary Card */}
                    <div className="info-card">
                        <h3 className="card-title">Order Summary</h3>
                        <div className="summary-content">
                            <div className="summary-row">
                                <span className="summary-label">Product Price</span>
                                <span className="summary-value">
                                    {formatCurrency(transaction.subtotal || transaction.total_transaction - (transaction.total_shipping || 0))}
                                </span>
                            </div>
                            <div className="summary-row">
                                <span className="summary-label">Delivery Fee ({transaction.shipping_by || "Expedisi"})</span>
                                <span className="summary-value">
                                    {formatCurrency(transaction.total_shipping || 0)}
                                </span>
                            </div>
                            {transaction.discount && transaction.discount > 0 && (
                                <div className="summary-row discount">
                                    <span className="summary-label">Discount</span>
                                    <span className="summary-value">-{formatCurrency(transaction.discount)}</span>
                                </div>
                            )}
                            {transaction.tax && transaction.tax > 0 && (
                                <div className="summary-row">
                                    <span className="summary-label">Tax</span>
                                    <span className="summary-value">{formatCurrency(transaction.tax)}</span>
                                </div>
                            )}
                            <div className="summary-divider"></div>
                            <div className="summary-row total">
                                <span className="summary-label">Total Transaction</span>
                                <span className="summary-value">{formatCurrency(transaction.total_transaction)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TransactionDetail;